import type {ReactElement, RefObject} from 'react';
import React, {useContext, useEffect, useRef} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {SocialMediaWizardContentSize, SocialMediaWizardMediaTypeSelection, WizardMobileNavView} from '@Components/social-media-wizard/social-media-wizard.types';
import {usePostPublishingParamsForAccount, useReelPublishingParamsForAccount, useStoryPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import {SocialMediaPostPublishParams} from '@Components/social-media-wizard/components/social-media-post-publish-params';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {
  setPublishToPost,
  setPublishToReel,
  setPublishToStory,
  toggleToPostOnly,
  toggleToReelOnly,
  toggleToStoryOnly,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {usePlatformSelectionforAccount, useWizardMobileNavView} from '@Components/social-media-wizard/social-media-wizard.hooks';
import type {SocialPostPlatformSelectionTransitionEventDetail} from '@Components/social-media/social-media.types';
import {SocialPostPlatformSelectionTransitionEvent} from '@Components/social-media/social-media.types';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {useAppDispatch} from '@/hooks';
import styles from './social-media-post-publish-params-wrapper.module.scss';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';

interface SocialMediaPostPublishParamsWrapperProps {
  account: SocialAccount;
  size?: SocialMediaWizardContentSize;
}

export function SocialMediaPostPublishParamsWrapper({account, size = SocialMediaWizardContentSize.XLARGE}: SocialMediaPostPublishParamsWrapperProps): ReactElement<any> {
  const storyRef = useRef<HTMLDivElement>(null);
  const postOrReelRef = useRef<HTMLDivElement>(null);
  const storyPublishingParamsForAccount = useStoryPublishingParamsForAccount(account.id);
  const postPublishingParamsForAccount = usePostPublishingParamsForAccount(account.id);
  const reelPublishingParamsForAccount = useReelPublishingParamsForAccount(account.id);
  const bothPrefixForKey = 'both-';
  const dispatch = useAppDispatch();
  const platformSelection = usePlatformSelectionforAccount(account.id);
  const wizardMobileNavView = useWizardMobileNavView();
  const {isSmallScreen, isExtraSmallScreen, isMediumScreen, isExtraLargeScreen, isLargeScreen} = useContext(SocialMediaWizardContainerQueriesContext);

  useEffect(() => {
    window.addEventListener(SocialPostPlatformSelectionTransitionEvent, onPlatformSelectionTransitionEvent);

    return (): void => {
      window.removeEventListener(SocialPostPlatformSelectionTransitionEvent, onPlatformSelectionTransitionEvent);
    };
  }, []);

  const onPlatformSelectionTransitionEvent = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const detail = customEvent.detail as SocialPostPlatformSelectionTransitionEventDetail;
    const platformMediaType = detail.platformSelection;
    const {accountId} = detail;

    if (account.id !== accountId) {
      return;
    }

    if ((platformMediaType === PlatformMediaType.POST || platformMediaType === PlatformMediaType.REELS) && postOrReelRef?.current) {
      runTransitionOnRef(postOrReelRef);
    }

    if (platformMediaType === PlatformMediaType.STORIES && storyRef?.current) {
      runTransitionOnRef(storyRef);
    }
  };

  const runTransitionOnRef = (ref: React.RefObject<HTMLDivElement | null>): void => {
    if (!ref?.current) {
      return;
    }

    ref.current.classList.add(styles.fadeIn);
  };

  const cleanupAnimation = (ref: React.RefObject<HTMLDivElement | null>): void => {
    if (!ref?.current) {
      return;
    }

    ref.current.classList.remove(styles.fadeIn);
  };

  const getPublishingParamsContainerMargin = (): string => {
    if (isExtraSmallScreen || isSmallScreen) {
      if (wizardMobileNavView === WizardMobileNavView.CONTENT) {
        return `spacing-m-b-8`;
      }
    }

    if (isMediumScreen || isLargeScreen || isExtraLargeScreen) {
      return `spacing-m-b-10`;
    }

    return `spacing-m-b-4`;
  };

  useDidUpdateEffect(() => {
    if (platformSelection === SocialMediaWizardMediaTypeSelection.POST) {
      dispatch(toggleToPostOnly(account.id));
    }

    if (platformSelection === SocialMediaWizardMediaTypeSelection.STORY) {
      dispatch(toggleToStoryOnly(account.id));
    }

    if (platformSelection === SocialMediaWizardMediaTypeSelection.REEL) {
      dispatch(toggleToReelOnly(account.id));
    }

    if (platformSelection === SocialMediaWizardMediaTypeSelection.POST_AND_STORY) {
      if (postPublishingParamsForAccount) {
        dispatch(setPublishToStory(account.id));
        return;
      }
      dispatch(setPublishToPost(account.id));
    }

    if (platformSelection === SocialMediaWizardMediaTypeSelection.REEL_AND_STORY) {
      if (reelPublishingParamsForAccount) {
        dispatch(setPublishToStory(account.id));
        return;
      }
      dispatch(setPublishToReel(account.id));
    }
  }, [platformSelection]);

  const shouldPublishReelOrPost = postPublishingParamsForAccount ?? reelPublishingParamsForAccount;
  const nonStoryMediaType = postPublishingParamsForAccount ? PlatformMediaType.POST : PlatformMediaType.REELS;

  const getPublishingParamsComponent = (platformMediaType: PlatformMediaType, key: string, refForWrapper: RefObject<HTMLDivElement | null>, classes = ''): ReactElement<any> => {
    return (
      <div
        onAnimationEnd={() => {
          cleanupAnimation(refForWrapper);
        }}
        data-platform-media-type={platformMediaType}
        ref={refForWrapper}
        className={`${styles.wrapperClasses} ${classes}`}
      >
        <SocialMediaPostPublishParams key={key} account={account} size={size} platformMediaType={platformMediaType} />
      </div>
    );
  };

  if (shouldPublishReelOrPost && !storyPublishingParamsForAccount) {
    return getPublishingParamsComponent(nonStoryMediaType, `${bothPrefixForKey}${nonStoryMediaType}`, postOrReelRef);
  }

  if (storyPublishingParamsForAccount && !shouldPublishReelOrPost) {
    return getPublishingParamsComponent(PlatformMediaType.STORIES, `${bothPrefixForKey}${SocialMediaWizardMediaTypeSelection.STORY}`, storyRef);
  }

  return (
    <>
      {getPublishingParamsComponent(nonStoryMediaType, nonStoryMediaType, postOrReelRef, getPublishingParamsContainerMargin())}
      {getPublishingParamsComponent(PlatformMediaType.STORIES, PlatformMediaType.STORIES, storyRef)}
    </>
  );
}
